import { Component } from '@verndale/core';
import YouTubeIframeLoader from 'youtube-iframe';
import Player from '@vimeo/player';

type YouTubePlayer = {
  on: (event: string, callback: () => void) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  mute: () => void;
};

class VideoModule extends Component {
  videoId: string | undefined = undefined;
  provider: string | undefined = undefined;
  player: YouTubePlayer | Player | undefined;
  isPlaying: boolean;
  playLabel: string;
  pauseLabel: string;
  videoReady: boolean = false;

  constructor(el: HTMLElement) {
    super(el);
    if (this.el.dataset.textural && this.el.dataset.textural === 'true') this.isPlaying = true;
    else this.isPlaying = false;
    this.playLabel = this.el.dataset.playLabel || 'Play';
    this.pauseLabel = this.el.dataset.pauseLabel || 'Pause';
    if (this.dom.playButtonLabel)
      (this.dom.playButtonLabel as HTMLElement).textContent = this.isPlaying
        ? this.pauseLabel
        : this.playLabel;

    this.provider = (this.dom.el as HTMLElement).dataset.provider;
    this.videoId = (this.dom.el as HTMLElement).dataset.videoId;
    this.buildHtml();
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      videoPlaceholder: this.el.querySelector('.video-placeholder') as HTMLElement,
      backgroundImage: this.el.querySelector('.picture') as HTMLImageElement,
      playButton: this.el.querySelector('.play-button') as HTMLElement,
      playButtonLabel: this.el.querySelector('.play-button-label') as HTMLElement,
      playIcon: this.el.querySelector('.play-icon') as HTMLElement,
      pauseIcon: this.el.querySelector('.pause-icon') as HTMLElement,
      localVideo: this.el.querySelector('video') as HTMLVideoElement
    };
  }

  addListeners() {
    (this.dom.playButton as HTMLButtonElement)?.addEventListener(
      'click',
      this.handlePlayButtonClick.bind(this)
    );

    if (this.dom.localVideo && (this.dom.localVideo as HTMLVideoElement).readyState === 4) {
      (this.el as HTMLElement).classList.add('loaded');
      (this.dom.playButton as HTMLElement).removeAttribute('disabled');
      this.videoReady = true;
    }
    //add load listener to localVideo
    if (this.dom.localVideo && (this.dom.localVideo as HTMLVideoElement).readyState < 3)
      (this.dom.localVideo as HTMLVideoElement).addEventListener('loadeddata', () => {
        (this.el as HTMLElement).classList.add('loaded');
        (this.dom.playButton as HTMLElement).removeAttribute('disabled');
        this.videoReady = true;
      });
  }

  checkIconsAndLabelsState() {
    if (this.dom.playButtonLabel as HTMLElement)
      (this.dom.playButtonLabel as HTMLElement).innerHTML = this.isPlaying
        ? this.pauseLabel
        : this.playLabel;

    if (this.dom.playIcon && this.dom.pauseIcon) {
      if (this.isPlaying) {
        (this.dom.playIcon as HTMLElement).style.display = 'none';
        (this.dom.pauseIcon as HTMLElement).style.display = 'block';
      } else {
        (this.dom.playIcon as HTMLElement).style.display = 'block';
        (this.dom.pauseIcon as HTMLElement).style.display = 'none';
      }
    }
  }

  buildHtml() {
    if (this.provider === 'vimeo') {
      this.initVimeoVideo();
    } else if (this.provider === 'youtube') {
      this.initYoutubeVideo();
    }
    if (this.dom.localVideo) {
      this.initLocalVideo();
    }
    this.checkIconsAndLabelsState();
  }

  initLocalVideo() {
    (this.el as HTMLElement).classList.add('loaded');
    if (this.dom.playButton) {
      (this.dom.playButton as HTMLElement).removeAttribute('disabled');
    }
    this.videoReady = true;
    if (this.el.dataset.textural && this.el.dataset.textural === 'true') {
      (this.dom.videoPlaceholder as HTMLElement).style.display = 'block';
    }
  }

  initYoutubeVideo() {
    YouTubeIframeLoader.load(YT => {
      this.player = new YT.Player(this.dom.videoPlaceholder, {
        height: '100%',
        width: '100%',
        host: 'https://www.youtube-nocookie.com',
        videoId: this.videoId,
        origin: window.location.origin,
        playerVars: {
          controls: 1,
          rel: 0
        },
        events: {
          onReady: () => {
            (this.dom.playButton as HTMLElement).removeAttribute('disabled');
            (this.dom.el as HTMLElement).classList.add('loaded');
            this.videoReady = true;
          },
          onStateChange: (event: { data: unknown }) => {
            if (event.data === YT.PlayerState.ENDED) {
              this.isPlaying = false;
              this.checkIconsAndLabelsState();
            }
          }
        }
      });
    });
  }

  initVimeoVideo() {
    const options = {
      id: parseInt(this.videoId as string)
    };

    const player = new Player(this.dom.videoPlaceholder as HTMLElement, options);
    player.on('loaded', () => {
      (this.dom.playButton as HTMLElement).removeAttribute('disabled');
      (this.dom.el as HTMLElement).classList.add('loaded');
      this.videoReady = true;
    });
    this.player = player;
  }

  handlePlayButtonClick() {
    if (!this.videoReady) return;
    if (this.dom.backgroundImage)
      (this.dom.backgroundImage as HTMLImageElement).style.display = 'none';
    if (this.isPlaying) {
      if (this.provider === 'vimeo') {
        (this.player as Player).pause();
      } else if (this.provider === 'youtube') {
        (this.player as YouTubePlayer).pauseVideo();
      } else if (this.dom.localVideo) {
        (this.dom.localVideo as HTMLVideoElement).pause();
      }
      this.isPlaying = false;
    } else if (!this.isPlaying) {
      if (this.provider === 'vimeo') {
        (this.player as Player).play();
        (this.dom.videoPlaceholder as HTMLElement).style.display = 'block';
      } else if (this.provider === 'youtube') {
        (this.player as YouTubePlayer).playVideo();
        (this.dom.videoPlaceholder as HTMLElement).style.display = 'block';
        if (this.el.querySelector('.video-placeholder'))
          (this.el.querySelector('.video-placeholder') as HTMLElement).style.display = 'block';
      } else if (this.dom.localVideo) {
        (this.dom.localVideo as HTMLVideoElement).play();
        (this.dom.videoPlaceholder as HTMLElement).style.display = 'block';
      }
      this.isPlaying = true;
    }
    if (this.dom.playButton) {
      if (this.el.dataset.textural && this.el.dataset.textural === 'true') {
        if (!this.isPlaying) {
          (this.dom.playIcon as HTMLElement).style.display = 'none';
          (this.dom.pauseIcon as HTMLElement).style.display = 'block';
        } else {
          (this.dom.playIcon as HTMLElement).style.display = 'block';
          (this.dom.pauseIcon as HTMLElement).style.display = 'none';
        }
      } else {
        (this.dom.playButton as HTMLElement).style.display = 'none';
      }
    }

    this.checkIconsAndLabelsState();
  }
}

export default VideoModule;
